"use client";

// First-party, cookie-free event tracking. Buffers events client-side and
// flushes them in a single batched request rather than one HTTP call per
// click — see the "efficient" requirement in the analytics plan.

const ANON_ID_KEY = "nohaya-anon-id";
const NO_TRACK_KEY = "nohaya-no-track";
const COLLECT_URL = "/api/analytics/collect";
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_AT_BUFFER_SIZE = 10;

// Self-exclusion for site owners/testers: visit any page once with
// ?no-track=1 to set a permanent opt-out in this browser (works across
// devices/IPs, unlike an IP-based exclude-list). Shared localStorage key
// with site-shared/js/analytics.js (the PHP-served pages' tracker) since
// both run on the same origin (www.nohaya.com) -- opting out on either
// side opts out everywhere.
function isOptedOut(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("no-track") === "1") {
      localStorage.setItem(NO_TRACK_KEY, "1");
    }
    return localStorage.getItem(NO_TRACK_KEY) === "1";
  } catch {
    return false;
  }
}

// Mirrors every event already flowing through this tracker into GA as a
// real gtag event, rather than instrumenting GA separately at each call
// site (view_pdf_click, download_click, the payment_* funnel, etc. all
// pick this up for free). window.gtag is defined by the inline script in
// app/layout.tsx; guarded with `?.` since gtag.js loads `afterInteractive`
// and may not have run yet for an event fired very early on the page.
function sendToGtag(eventName: string, path: string, metadata?: Record<string, unknown>) {
  (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag?.("event", eventName, {
    ...metadata,
    page_path: path,
  });
}

type BufferedEvent = { type: string; path: string; anonId: string; metadata?: Record<string, unknown> };

let buffer: BufferedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersAttached = false;

// Per-page engagement -- how long a visitor stayed, whether they scrolled,
// whether they clicked anything. Deliberately NOT one event per scroll/click
// (that would be a network call per interaction); instead a handful of
// numbers are updated in memory by passive/cheap listeners and folded into
// a single "page_engagement" event the moment the visitor leaves that page,
// which then rides the existing batched flush -- zero additional network
// calls beyond what the tracker already does.
type Engagement = { path: string; startTime: number; maxScrollPct: number; clicked: boolean };
let engagement: Engagement | null = null;
let engagementListenersAttached = false;
let scrollRafScheduled = false;

function computeScrollPct(): number {
  const doc = document.documentElement;
  const scrollableHeight = doc.scrollHeight - doc.clientHeight;
  if (scrollableHeight <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((window.scrollY / scrollableHeight) * 100)));
}

function ensureEngagementListeners() {
  if (engagementListenersAttached || typeof window === "undefined") return;
  engagementListenersAttached = true;
  // rAF-batched: a scroll firing dozens of times a second only ever does at
  // most one cheap DOM read per frame, no matter how fast it fires.
  window.addEventListener(
    "scroll",
    () => {
      if (scrollRafScheduled || !engagement) return;
      scrollRafScheduled = true;
      requestAnimationFrame(() => {
        scrollRafScheduled = false;
        if (!engagement) return;
        const pct = computeScrollPct();
        if (pct > engagement.maxScrollPct) engagement.maxScrollPct = pct;
      });
    },
    { passive: true }
  );
  // Single delegated listener, capture phase, just flips a flag -- no
  // per-click work, no per-click network call.
  document.addEventListener(
    "click",
    () => {
      if (engagement) engagement.clicked = true;
    },
    { passive: true, capture: true }
  );
}

const MIN_ENGAGEMENT_MS = 250;

function flushEngagement() {
  if (!engagement) return;
  const { path, startTime, maxScrollPct, clicked } = engagement;
  engagement = null;
  const durationMs = Date.now() - startTime;
  // Skip near-instant bounces (e.g. a page that immediately redirects) --
  // not a meaningful visit worth a row.
  if (durationMs < MIN_ENGAGEMENT_MS) return;
  buffer.push({
    type: "page_engagement",
    path,
    anonId: getAnonId(),
    metadata: { durationMs, maxScrollPct, clicked: clicked ? 1 : 0 },
  });
}

function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown-anon";
  }
}

function ensureListeners() {
  if (listenersAttached || typeof document === "undefined") return;
  listenersAttached = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushEngagement();
      flush();
    }
  });
  window.addEventListener("pagehide", () => {
    flushEngagement();
    flush();
  });
}

function ensureFlushTimer() {
  if (flushTimer || typeof window === "undefined") return;
  flushTimer = setInterval(() => {
    if (buffer.length) flush();
  }, FLUSH_INTERVAL_MS);
}

function flush() {
  if (!buffer.length) return;
  const events = buffer;
  buffer = [];
  const body = JSON.stringify({ events });
  try {
    const blob = new Blob([body], { type: "text/plain" });
    const sent = navigator.sendBeacon?.(COLLECT_URL, blob);
    if (!sent) {
      fetch(COLLECT_URL, { method: "POST", body, keepalive: true }).catch(() => {});
    }
  } catch {
    // Never let a tracking failure surface into the calling UI code.
  }
}

export function trackEvent(type: string, metadata?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  if (isOptedOut()) return;
  ensureListeners();
  ensureFlushTimer();
  const path = window.location.pathname;
  buffer.push({ type, path, anonId: getAnonId(), metadata });
  if (buffer.length >= FLUSH_AT_BUFFER_SIZE) flush();
  sendToGtag(type, path, metadata);
}

// Called on every route change (see AnalyticsTracker.tsx). Closes out
// engagement tracking for whatever page the visitor is leaving (folding
// duration/scroll/click into one event), records the new page_view, then
// starts a fresh engagement window for the new page -- an SPA route change
// never fires pagehide/visibilitychange, so this is the only hook point
// for "the visitor left page A for page B" within the same tab.
export function trackPageView() {
  if (typeof window === "undefined") return;
  if (isOptedOut()) return;
  ensureListeners();
  ensureEngagementListeners();
  ensureFlushTimer();
  flushEngagement();
  const path = window.location.pathname;
  buffer.push({ type: "page_view", path, anonId: getAnonId() });
  engagement = { path, startTime: Date.now(), maxScrollPct: 0, clicked: false };
  if (buffer.length >= FLUSH_AT_BUFFER_SIZE) flush();
  // GA config disables its own automatic page_view (send_page_view: false)
  // so this stays the single source of truth -- otherwise the first load
  // would be double-counted (GA's automatic hit + this one).
  sendToGtag("page_view", path);
}
